import { useSyncExternalStore } from 'react'

/**
 * Тон плашки. Повторяет ось `ToastStatusTone` примитива `shared/ui/Toast` и намеренно не
 * импортирует её: `shared/lib` о разметке не знает. Расхождение поймает `tsc` в хосте —
 * там значение уходит прямо в проп `tone`.
 *
 * `pending` в очередь не входит: инверсная плашка живёт ровно столько, сколько идёт операция,
 * и снимает её тот экран, который операцию запустил, — очередь про его состояние не знает.
 */
export type ToastTone = 'accent' | 'success' | 'warning' | 'danger'

/** Действие внутри плашки. Подпись — одно-два слова, иначе на 360px не остаётся текста. */
export type ToastAction = {
  label: string
  onClick: () => void
}

export type ShowToastOptions = {
  tone?: ToastTone
  /**
   * Живость. `assertive` (`role="alert"`) — начатое остановилось: 1С не ответила, заказ не ушёл.
   * Обычный исход остаётся `polite` и читается в порядке очереди.
   */
  announce?: 'polite' | 'assertive'
  /**
   * Окно жизни в мс. Не задано — считает сама плашка: 4 с, 8 с при действии.
   * `0` — не исчезает сама, остаётся крестик.
   */
  duration?: number
  action?: ToastAction
}

export type ToastItem = ShowToastOptions & {
  id: string
  /** Готовая фраза из i18n. Очередь строк не склеивает и не переводит. */
  message: string
}

/**
 * Сколько плашек видно одновременно. Остальные ждут своей очереди: стопка из восьми
 * перекрывает экран и всё равно не читается. Число из docs/ui-kit/contracts/toast.md.
 */
const MAX_VISIBLE = 3

let queue: ToastItem[] = []

/**
 * Видимая часть, посчитанная один раз на изменение. Считать её в `getSnapshot` нельзя:
 * `slice()` возвращал бы новый массив на каждый вызов, а `useSyncExternalStore` сверяет
 * снимки по ссылке и ушёл бы в бесконечный рендер.
 */
let visible: readonly ToastItem[] = []

const listeners = new Set<() => void>()

function commit(next: ToastItem[]): void {
  queue = next
  visible = next.length > MAX_VISIBLE ? next.slice(0, MAX_VISIBLE) : next
  listeners.forEach((listener) => listener())
}

/**
 * Показать уведомление. Обычная функция, а не хук: тост поднимается и из обработчика
 * компонента, и из `onQueryStarted` мутации, и из middleware — контекста там нет.
 *
 * Текст приходит готовым от вызывающего, ключом i18n: очередь строк не знает (правило 5).
 * Возвращает идентификатор — по нему плашку можно снять раньше срока.
 */
export function showToast(message: string, options: ShowToastOptions = {}): string {
  const id = crypto.randomUUID()

  /*
   * Схлопывание дублей: три одинаковых «Нет связи с 1С» подряд — одно событие, повторённое
   * трижды. Прошлая плашка снимается, новая встаёт в конец с новым id, то есть с новым
   * отсчётом и новым объявлением — React смонтирует другой узел.
   */
  const withoutDuplicate = queue.filter(
    (item) => item.message !== message || item.tone !== options.tone,
  )

  commit([...withoutDuplicate, { ...options, id, message }])

  return id
}

/** Снять плашку: истёк её таймер, нажат крестик, действие выполнено. */
export function dismissToast(id: string): void {
  const next = queue.filter((item) => item.id !== id)
  if (next.length === queue.length) return

  commit(next)
}

/** Видимая часть стопки. Хвост очереди ждёт, пока не освободится место. */
export function getVisibleToasts(): readonly ToastItem[] {
  return visible
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Подписка хоста на очередь. Единственный потребитель — контейнер в `app`; экраны
 * очередь не читают, они только зовут `showToast`.
 */
export function useToasts(): readonly ToastItem[] {
  return useSyncExternalStore(subscribe, getVisibleToasts, getVisibleToasts)
}

/** Только для тестов: очередь — модульный синглтон и между сценариями сама не сбрасывается. */
export function resetToasts(): void {
  commit([])
}
